import React, { useState, useEffect, useMemo } from 'react';
import '../css/characterSelector.css';

import LRUCache from '../components/cache.js';
import renderStats from '../components/renderStats.js';

const seasoniteCache = new LRUCache(30);

const DEFAULT_SEASONITE_DATA = {
  color: "None",
  rune: "None",
  rarity: "None",
  displayColor: "Click",
  displayRune: "to select",
  stats: [],
  buffs: [],
};

const SeasoniteWrapper = ({ getSeasoniteData: passSeasoniteData, context }) => {
  const [seasoniteData, setSeasoniteData] = useState(DEFAULT_SEASONITE_DATA);
  const [colorList, setColorList] = useState(null);
  const [selectedColor, setSelectedColor] = useState("Red");
  const [selectedRarity, setSelectedRarity] = useState("rare");
  const [selectedType, setSelectedType] = useState("offense");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [runeList, setRuneList] = useState(null);


  // Get details of selected artifact
  async function fetchSelectedSeasonite(color, rarity, type, rune) {
    const cacheKey = JSON.stringify({ color, rarity, type: type, rune });
    const cached = seasoniteCache.get(cacheKey);
    if (cached) return cached;

    try {
      const res = await fetch('/api/seasonite/getSeasonite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: cacheKey
      });

      if (!res.ok) {
        console.error('Failed to fetch seasonite data');
        return null;
      }

      const data = await res.json();
      seasoniteCache.set(cacheKey, data);
      return data;

    } catch (err) {
      console.error('Error fetching seasonite:', err);
      return null;
    }
  }

  async function setSeasonite(rune) {
    let seasonite = await fetchSelectedSeasonite(selectedColor, selectedRarity, selectedType, rune)
    setSeasoniteData(seasonite)

  }

  // Clear the current selection back to the empty/default state
  function deselectSeasonite() {
    setSeasoniteData(DEFAULT_SEASONITE_DATA);
    setDropdownOpen(false);
  }

  async function fetchSeasoniteColors() {
    const res = await fetch('/api/seasonite/getColors', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!res.ok) {
      console.error('Failed to fetch seasonite color list');
      return [];
    }
    return await res.json();
  }

  async function fetchRunes(rarity, type) {
    const cacheKey = JSON.stringify({ rarity, type });
    const cached = seasoniteCache.get(cacheKey);
    if (cached) return cached;

    try {
      const res = await fetch('/api/seasonite/getRunes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: cacheKey
      });

      if (!res.ok) {
        console.error('Failed to fetch rune data');
        return null;
      }

      const data = await res.json();
      seasoniteCache.set(cacheKey, data);
      return data;

    } catch (err) {
      console.error('Error fetching artifact:', err);
      return null;
    }
  }





  useEffect(() => {
    // Load list of all seasoniteColors
    fetchSeasoniteColors().then(setColorList);
  }, []);


  const grabImg = (folder, subfolder, imgName) => {
    const path = subfolder.replace(/ /g, "_");
    //Artifacts don't have a subfolder
    if (imgName === "_Thumbnail") {
      return `/img/${folder}/${path}${imgName}.webp`;
    }
    else {
      return `/img/${folder}/${path}/${imgName}.webp`;
    }
  };


  useEffect(() => {
    fetchRunes(selectedRarity, selectedType).then(setRuneList);
  }, [selectedRarity, selectedType])

  useEffect(() => {
    if (colorList) {
      setSelectedType(colorList[selectedColor]?.type);
      if (!colorList[selectedColor]?.rarities.includes(selectedRarity)) {
        setSelectedRarity(colorList[selectedColor]?.rarities[0]);
      }
    }
  }, [selectedColor])

  useEffect(() => {
    const buffs = [...(seasoniteData.buffs ?? [])];
    const cleanedStats = [];
    //separate targeted stats into buffs
    for (const stat of seasoniteData.stats ?? []) {
      if (stat.target?.type) {
        buffs.push({
          name: `Seasonite Secondary Stat`,
          type: "Seasonite",
          hidden: true,
          stats: [
            {
              key: stat.key,
              type: `Seasonite Secondary Stat`,
              value: stat.value,
              target: stat.target,
            },
          ],
        });
      } else {
        cleanedStats.push(stat);
      }
    }

    passSeasoniteData({
      stats: cleanedStats,
      buffs,
    });
  }, [seasoniteData, passSeasoniteData]);

  return (
    <span className="characterWrapper">
      {/* Display if Seasonite data is available */}
      {seasoniteData ? (
        <>
          {/* Seasonite name and image */}
          <div className={`characterContainer rarityBgSoft rarityBorder ${seasoniteData.rarity.toLowerCase()} artifact`}>
            <div className={`characterHeader rarityBorder ${seasoniteData.rarity.toLowerCase()}`}>Seasonite</div>
            <div className="selectedName">{seasoniteData.displayColor}: {seasoniteData.displayRune}</div>
            <div className="seasoniteImageStack">
              {/* Seasonite Gem Image */}
              <img
                src={grabImg("Seasonite", "Gems", `${seasoniteData.color}_${seasoniteData.rarity}`)}
                alt={seasoniteData.name}
                onError={(e) => {
                  e.currentTarget.onerror = null;

                }}
                className={`seasoniteImage rarityBorder rarityBg ${seasoniteData.rarity.toLowerCase()}`}
                onClick={() => setDropdownOpen(true)}
                style={{ cursor: 'pointer' }}
              />
              {/* Seasonite Rune Image */}
              {seasoniteData.rune !== "None" ? (
                <img
                  src={grabImg("Seasonite", "Runes", `${seasoniteData.rune}`)}
                  alt={seasoniteData.rune}
                  className={`seasoniteRune`}
                  onClick={() => setDropdownOpen(true)}
                  style={{ cursor: 'pointer' }}
                />
              ) : null}
            </div>

            {seasoniteData.stats.length !== 0 ? (
              <>
                <div className={`artifact rarityBorder rarityBgDark baseStat ${seasoniteData.rarity.toLowerCase()}`}>
                  Base Stats
                </div>
                <span className="statColorOverride artifactStat">
                  {renderStats(seasoniteData.stats)}
                </span>
              </>) : null}

            {seasoniteData.buffs.length !== 0 ? (
              <>
                <span className={`roleDisplay rarityBorder rarityBgDark ${seasoniteData.rarity.toLowerCase()}`}>
                  Seasonite Buffs
                </span>
                {seasoniteData.buffs.map((buff) => (
                  <div className="buffItem" key={buff.name}>
                    <div className="buffHeader">
                      <span className="buffName">-{buff.name}</span>
                      {buff.stackLimit && (
                        <span className={`buffStackLimit rarityText ${seasoniteData.rarity.toLowerCase()}`}>Max: {buff.stackLimit}</span>
                      )}
                    </div>

                    <div className="statColorOverride buffStats">
                      {renderStats(buff.stats)}
                    </div>
                  </div>
                ))}
              </>
            ) : null}

            <div className='seasoniteBottomGap'></div>






          </div>
        </>
      ) : (
        "Loading..."
      )}


      {/* selection modal */}
      {dropdownOpen && (
        <div className="cookieModalOverlay" onClick={() => setDropdownOpen(false)}>
          <div className="cookieModalContent" onClick={(e) => e.stopPropagation()}>

            {/* Deselect current Seasonite */}
            {seasoniteData.rune !== "None" ? (
              <div className="seasoniteDeselectRow">
                <button
                  type="button"
                  className="deselectButton"
                  onClick={deselectSeasonite}
                >
                  Remove Seasonite
                </button>
              </div>
            ) : null}

            <div className="seasoniteDropdownGrid">

              {/* Seasonite Colors */}
              <div className="seasoniteColumn column1">
                <div className="columnHeader">Colors</div>

                {colorList && Object.entries(colorList).map(([key, color]) => (
                  <div
                    key={key}
                    className="selectableItem"
                    onClick={() => setSelectedColor(key)}
                  >
                    <div className="selectedColor"
                      style={{
                        border: key === selectedColor ? "2px solid green" : "0px white",
                        borderRadius: "6px"
                      }}
                    >
                      <img
                        src={grabImg("Seasonite", "Icons", key)}
                        className={"seasoniteIcon"}
                        alt={color.name}
                      />
                    </div>
                    <span>{color.name}</span>
                  </div>
                ))}
              </div>

              {/* Rarities */}
              <div className="seasoniteColumn column2">
                <div className="columnHeader">Rarity</div>

                {selectedColor && (
                  <>
                    {colorList[selectedColor]?.rarities?.map((rarity) => (
                      <div
                        key={rarity}
                        className="selectableItem"
                        onClick={() => setSelectedRarity(rarity)}
                      >
                        <div className="selectedGem"
                          style={{
                            border: rarity === selectedRarity ? "2px solid green" : "0px white",
                            borderRadius: "6px"
                          }}
                        >
                          <img
                            src={grabImg("Seasonite", "Gems", `${selectedColor}_${rarity}`)}
                            alt={rarity}
                            className={`seasoniteRarity rarityBg rarityBorder ${rarity.toLowerCase()}`}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Runes */}
              <div className="seasoniteColumn column3">
                <div className="columnHeader">Runes</div>
                <div className="runeGrid">
                  {Object.entries(runeList).map(([key, name]) => (
                    <div
                      key={key}
                      className="cookieImageWrapper"
                      style={{
                        border: key === null ? "2px solid green" : "0px white",
                        borderRadius: "6px"
                      }}
                    >
                      <img
                        src={grabImg("Seasonite", "Runes", key)}
                        alt={name}
                        className="dropdownImage rarityBg rarityBorder black"
                        onClick={() => {
                          setSeasonite(key)
                          setDropdownOpen(false);
                        }}
                      />
                      <div className="runeName">
                        {key.replace(/_/g, ' ')}
                      </div>
                      <div className="nameTooltip" style={{ bottom: "-10px" }}>
                        {name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

          </div>
        </div>
      )}
    </span>
  );
};

export default SeasoniteWrapper;